import { jsx } from 'react/jsx-runtime';
import { composeEventHandlers } from '@radix-ui/primitive';
import { composeRefs } from '@radix-ui/react-compose-refs';
import { createContextScope } from '@radix-ui/react-context';
import { useId } from '@radix-ui/react-id';
import { Primitive } from '@radix-ui/react-primitive';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import * as React from 'react';
import { createMenuScope, Anchor, Content, Group, Label, Item, CheckboxItem, RadioGroup, RadioItem, ItemIndicator, Separator, Arrow, SubTrigger, SubContent, Portal, Sub, Root } from '../../menu/Menu.mjs';

const DROPDOWN_MENU_NAME = "DropdownMenu";
const [createDropdownMenuContext, createDropdownMenuScope] = createContextScope(
  DROPDOWN_MENU_NAME,
  [createMenuScope]
);
const useMenuScope = createMenuScope();
const [DropdownMenuProvider, useDropdownMenuContext] = createDropdownMenuContext(DROPDOWN_MENU_NAME);
const DropdownMenu = (props) => {
  const {
    __scopeDropdownMenu,
    children,
    dir,
    open: openProp,
    defaultOpen,
    onOpenChange,
    modal = true
  } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  const triggerRef = React.useRef(null);
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  return /* @__PURE__ */ jsx(
    DropdownMenuProvider,
    {
      scope: __scopeDropdownMenu,
      triggerId: useId(),
      triggerRef,
      contentId: useId(),
      open,
      onOpenChange: setOpen,
      onOpenToggle: React.useCallback(
        () => setOpen((prevOpen) => !prevOpen),
        [setOpen]
      ),
      modal,
      children: /* @__PURE__ */ jsx(
        Root,
        {
          ...menuScope,
          open,
          onOpenChange: setOpen,
          dir,
          modal,
          children
        }
      )
    }
  );
};
DropdownMenu.displayName = DROPDOWN_MENU_NAME;
const ANCHOR_NAME = "DropdownMenuAnchor";
const DropdownMenuAnchor = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...anchorProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(Anchor, { ...menuScope, ref: forwardedRef, ...anchorProps });
});
DropdownMenuAnchor.displayName = ANCHOR_NAME;
const TRIGGER_NAME = "DropdownMenuTrigger";
const DropdownMenuTrigger = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, disabled = false, ...triggerProps } = props;
  const context = useDropdownMenuContext(TRIGGER_NAME, __scopeDropdownMenu);
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(Anchor, { asChild: true, ...menuScope, children: /* @__PURE__ */ jsx(
    Primitive.button,
    {
      type: "button",
      id: context.triggerId,
      "aria-haspopup": "menu",
      "aria-expanded": context.open,
      "aria-controls": context.open ? context.contentId : void 0,
      "data-state": context.open ? "open" : "closed",
      "data-disabled": disabled ? "" : void 0,
      disabled,
      ...triggerProps,
      ref: composeRefs(forwardedRef, context.triggerRef),
      onPointerDown: composeEventHandlers(props.onPointerDown, (event) => {
        if (!disabled && event.button === 0 && event.ctrlKey === false) {
          context.onOpenToggle();
          if (!context.open) event.preventDefault();
        }
      }),
      onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
        if (disabled) return;
        if (["Enter", " "].includes(event.key)) context.onOpenToggle();
        if (event.key === "ArrowDown") context.onOpenChange(true);
        if (["Enter", " ", "ArrowDown"].includes(event.key))
          event.preventDefault();
      })
    }
  ) });
});
DropdownMenuTrigger.displayName = TRIGGER_NAME;
const PORTAL_NAME = "DropdownMenuPortal";
const DropdownMenuPortal = (props) => {
  const { __scopeDropdownMenu, ...portalProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(Portal, { ...menuScope, ...portalProps });
};
DropdownMenuPortal.displayName = PORTAL_NAME;
const CONTENT_NAME = "DropdownMenuContent";
const DropdownMenuContent = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...contentProps } = props;
  const context = useDropdownMenuContext(CONTENT_NAME, __scopeDropdownMenu);
  const menuScope = useMenuScope(__scopeDropdownMenu);
  const hasInteractedOutsideRef = React.useRef(false);
  return /* @__PURE__ */ jsx(
    Content,
    {
      id: context.contentId,
      "aria-labelledby": context.triggerId,
      ...menuScope,
      ...contentProps,
      ref: forwardedRef,
      onCloseAutoFocus: composeEventHandlers(props.onCloseAutoFocus, (event) => {
        if (!hasInteractedOutsideRef.current)
          context.triggerRef.current?.focus();
        hasInteractedOutsideRef.current = false;
        event.preventDefault();
      }),
      onInteractOutside: composeEventHandlers(
        props.onInteractOutside,
        (event) => {
          const originalEvent = event.detail.originalEvent;
          const ctrlLeftClick = originalEvent.button === 0 && originalEvent.ctrlKey === true;
          const isRightClick = originalEvent.button === 2 || ctrlLeftClick;
          if (!context.modal || isRightClick)
            hasInteractedOutsideRef.current = true;
        }
      ),
      style: {
        ...props.style,
        // re-namespace exposed content custom properties
        ...{
          "--radix-dropdown-menu-content-transform-origin": "var(--fx-popper-transform-origin)",
          "--radix-dropdown-menu-content-available-width": "var(--fx-popper-available-width)",
          "--radix-dropdown-menu-content-available-height": "var(--fx-popper-available-height)",
          "--radix-dropdown-menu-trigger-width": "var(--fx-popper-anchor-width)",
          "--radix-dropdown-menu-trigger-height": "var(--fx-popper-anchor-height)"
        }
      }
    }
  );
});
DropdownMenuContent.displayName = CONTENT_NAME;
const GROUP_NAME = "DropdownMenuGroup";
const DropdownMenuGroup = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...groupProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(Group, { ...menuScope, ...groupProps, ref: forwardedRef });
});
DropdownMenuGroup.displayName = GROUP_NAME;
const LABEL_NAME = "DropdownMenuLabel";
const DropdownMenuLabel = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...labelProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(Label, { ...menuScope, ...labelProps, ref: forwardedRef });
});
DropdownMenuLabel.displayName = LABEL_NAME;
const ITEM_NAME = "DropdownMenuItem";
const DropdownMenuItem = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...itemProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(Item, { ...menuScope, ...itemProps, ref: forwardedRef });
});
DropdownMenuItem.displayName = ITEM_NAME;
const CHECKBOX_ITEM_NAME = "DropdownMenuCheckboxItem";
const DropdownMenuCheckboxItem = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...checkboxItemProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(
    CheckboxItem,
    {
      ...menuScope,
      ...checkboxItemProps,
      ref: forwardedRef
    }
  );
});
DropdownMenuCheckboxItem.displayName = CHECKBOX_ITEM_NAME;
const RADIO_GROUP_NAME = "DropdownMenuRadioGroup";
const DropdownMenuRadioGroup = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...radioGroupProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(
    RadioGroup,
    {
      ...menuScope,
      ...radioGroupProps,
      ref: forwardedRef
    }
  );
});
DropdownMenuRadioGroup.displayName = RADIO_GROUP_NAME;
const RADIO_ITEM_NAME = "DropdownMenuRadioItem";
const DropdownMenuRadioItem = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...radioItemProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(
    RadioItem,
    {
      ...menuScope,
      ...radioItemProps,
      ref: forwardedRef
    }
  );
});
DropdownMenuRadioItem.displayName = RADIO_ITEM_NAME;
const INDICATOR_NAME = "DropdownMenuItemIndicator";
const DropdownMenuItemIndicator = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...itemIndicatorProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(
    ItemIndicator,
    {
      ...menuScope,
      ...itemIndicatorProps,
      ref: forwardedRef
    }
  );
});
DropdownMenuItemIndicator.displayName = INDICATOR_NAME;
const SEPARATOR_NAME = "DropdownMenuSeparator";
const DropdownMenuSeparator = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...separatorProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(
    Separator,
    {
      ...menuScope,
      ...separatorProps,
      ref: forwardedRef
    }
  );
});
DropdownMenuSeparator.displayName = SEPARATOR_NAME;
const ARROW_NAME = "DropdownMenuArrow";
const DropdownMenuArrow = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...arrowProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(Arrow, { ...menuScope, ...arrowProps, ref: forwardedRef });
});
DropdownMenuArrow.displayName = ARROW_NAME;
const DropdownMenuSub = (props) => {
  const {
    __scopeDropdownMenu,
    children,
    open: openProp,
    onOpenChange,
    defaultOpen
  } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  return /* @__PURE__ */ jsx(Sub, { ...menuScope, open, onOpenChange: setOpen, children });
};
const SUB_TRIGGER_NAME = "DropdownMenuSubTrigger";
const DropdownMenuSubTrigger = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...subTriggerProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(
    SubTrigger,
    {
      ...menuScope,
      ...subTriggerProps,
      ref: forwardedRef
    }
  );
});
DropdownMenuSubTrigger.displayName = SUB_TRIGGER_NAME;
const SUB_CONTENT_NAME = "DropdownMenuSubContent";
const DropdownMenuSubContent = React.forwardRef((props, forwardedRef) => {
  const { __scopeDropdownMenu, ...subContentProps } = props;
  const menuScope = useMenuScope(__scopeDropdownMenu);
  return /* @__PURE__ */ jsx(
    SubContent,
    {
      ...menuScope,
      ...subContentProps,
      ref: forwardedRef,
      style: {
        ...props.style,
        // re-namespace exposed content custom properties
        ...{
          "--radix-dropdown-menu-content-transform-origin": "var(--fx-popper-transform-origin)",
          "--radix-dropdown-menu-content-available-width": "var(--fx-popper-available-width)",
          "--radix-dropdown-menu-content-available-height": "var(--fx-popper-available-height)",
          "--radix-dropdown-menu-trigger-width": "var(--fx-popper-anchor-width)",
          "--radix-dropdown-menu-trigger-height": "var(--fx-popper-anchor-height)"
        }
      }
    }
  );
});
DropdownMenuSubContent.displayName = SUB_CONTENT_NAME;

export { DropdownMenu, DropdownMenuAnchor, DropdownMenuArrow, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuItemIndicator, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, createDropdownMenuScope };
//# sourceMappingURL=Dropdown.mjs.map
